import { appFeatures } from "@/mocks/appInfo";
import type { Notice } from "@/pages/home/types";
import SectionHeading from "@/pages/home/components/SectionHeading";

interface AppSectionProps {
  onOpenNotice: (notice: Notice) => void;
  onCopy: (text: string) => void;
}

const installNotice: Notice = {
  title: "App 安装方式",
  lines: [
    "安卓端：获取安装包后按系统提示完成安装；如提示来源限制，请在设置中允许本次安装。",
    "iOS 端：按页面给出的添加方式操作后，可从主屏幕图标直接打开。",
    "安装完成后 App 会自动记忆可用入口，线路变更时推送提醒，随时回到最新地址。",
  ],
  copyText: "51heilian-app.top/download",
  copyLabel: "复制下载地址",
};

export default function AppSection({ onOpenNotice, onCopy }: AppSectionProps) {
  return (
    <section id="app" className="w-full bg-background-50 py-12 md:py-16">
      <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6">
        <SectionHeading
          eyebrow="APP DOWNLOAD"
          title="装上 App，入口再也不走丢"
          desc="相比每次手动找网址，App 会自动测速并记忆可用入口，地址一变就能收到提醒，省事也省心。"
          icon="ri-smartphone-line"
        />

        <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:gap-10">
          <div className="order-2 lg:order-1">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4">
              {appFeatures.map((feature) => (
                <div
                  key={feature.id}
                  className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                    <i className={`${feature.icon} text-xl`}></i>
                  </span>
                  <div>
                    <h3 className="text-sm font-bold text-foreground-950">{feature.title}</h3>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-600">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => onOpenNotice(installNotice)}
                className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-download-2-line text-base"></i>
                获取安装方式
              </button>
              <button
                type="button"
                onClick={() => onCopy("51heilian-app.top/download")}
                className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm font-bold text-foreground-800 transition-colors hover:bg-background-100"
              >
                <i className="ri-file-copy-line text-base"></i>
                复制下载地址
              </button>
            </div>
          </div>

          <div className="order-1 flex justify-center lg:order-2">
            <div className="relative w-full max-w-[420px]">
              <div className="absolute -inset-3 rounded-lg bg-accent-100/70"></div>
              <div className="relative aspect-[9/11] w-full overflow-hidden rounded-lg border border-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Smartphone%20held%20in%20hand%20showing%20a%20dark%20modern%20app%20interface%20with%20warm%20red%20accent%20glow%2C%20moody%20studio%20background%2C%20cinematic%20product%20photography%2C%20high%20detail&width=900&height=1100&seq=hl-app-mock&orientation=portrait"
                  alt="51黑料网 app 下载安装界面展示"
                  title="51黑料网 app下载 安装方式"
                  className="h-full w-full animate-float-slow object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 left-4 right-4 flex items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3">
                <div>
                  <p className="text-xs text-foreground-500">当前版本</p>
                  <p className="text-sm font-bold text-foreground-950">v5.1 · 安卓 / iOS</p>
                </div>
                <span className="flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-xs font-bold text-accent-800">
                  <i className="ri-flashlight-fill"></i>
                  极速版
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}